import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ERoute } from 'common/const/route.enum';
import { Spin } from 'common/components';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { AuthCardLogo } from 'entities/Auth/components/AuthCardLogo';
import { SelfRegistrationProfile } from 'entities/Auth/components/SelfRegistration/SelfRegistrationProfile';
import { SelfRegistrationLegalEntities } from 'entities/Auth/components/SelfRegistration/SelfRegistrationLegalEntities';
import { SelfRegistrationAddresses } from 'entities/Auth/components/SelfRegistration/SelfRegistrationAddresses';
import { ESelfRegistrationStep, IUpdateProfilePayload } from 'entities/SelfRegistration/SelfRegistration.models';
import {
  completeAddresses,
  completeLegals,
  getSetSelfRegistrationStep,
  selectIsSelfRegistrationLoading,
  selectSelfRegistrationError,
  selectSelfRegistrationStep,
  setSelfRegistrationError,
  updateProfile,
} from 'entities/SelfRegistration/SelfRegistration.slice';
import { selectAuth, selectIsAuthLoading } from 'entities/Auth/Auth.slice';

const STEP_ITEMS = [{ title: 'Профиль' }, { title: 'Юридические лица' }, { title: 'Адреса доставки' }];
const TOTAL_STEPS = STEP_ITEMS.length;

const STEP_TO_INDEX: Record<string, number> = {
  [ESelfRegistrationStep.Profile]: 0,
  [ESelfRegistrationStep.Legals]: 1,
  [ESelfRegistrationStep.Addresses]: 2,
};

export const SelfRegistrationLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  // auth
  const auth = useAppSelector(selectAuth);
  const isAuthLoading = useAppSelector(selectIsAuthLoading);
  // selfRegistration
  const selfRegistrationStep = useAppSelector(selectSelfRegistrationStep);
  const isSelfRegistrationLoading = useAppSelector(selectIsSelfRegistrationLoading);
  const selfRegistrationError = useAppSelector(selectSelfRegistrationError);

  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetch = async () => {
      if (auth) {
        await dispatch(getSetSelfRegistrationStep());
      }

      setInitialLoading(false);
    };

    fetch();
  }, [auth]);

  const handleUpdateProfile = async (payload: IUpdateProfilePayload): Promise<void> => {
    dispatch(updateProfile(payload));
  };

  const handleCompleteLegals = async (): Promise<void> => {
    dispatch(completeLegals());
  };

  const handleCompleteAddresses = async (): Promise<void> => {
    dispatch(completeAddresses());
  };

  if ((!auth && isAuthLoading) || initialLoading) {
    return (
      <div className="h-full flex align-center justify-center">
        <Spin />
      </div>
    );
  }

  if (!auth && !isAuthLoading) {
    return <Navigate to={ERoute.Login} />;
  }

  if (auth && (!auth.access.isSelfRegistered || auth.access.selfRegistrationCompletedAt)) {
    return <Navigate to={ERoute.Profile} />;
  }

  if (selfRegistrationStep === ESelfRegistrationStep.Completed) {
    return <Navigate to={ERoute.Profile} />;
  }

  const currentStepIndex = selfRegistrationStep ? (STEP_TO_INDEX[selfRegistrationStep] ?? 0) : 0;

  const renderStep = () => {
    switch (selfRegistrationStep) {
      case ESelfRegistrationStep.Legals:
        return (
          <SelfRegistrationLegalEntities
            onNext={handleCompleteLegals}
            loading={isSelfRegistrationLoading}
            error={selfRegistrationError}
          />
        );
      case ESelfRegistrationStep.Addresses:
        return (
          <SelfRegistrationAddresses
            onFinish={handleCompleteAddresses}
            loading={isSelfRegistrationLoading}
            error={selfRegistrationError}
          />
        );
      default:
        return (
          <SelfRegistrationProfile
            onSubmit={handleUpdateProfile}
            loading={isSelfRegistrationLoading}
            error={selfRegistrationError}
            currentUserId={auth?.access.userId}
            onErrorClear={() => dispatch(setSelfRegistrationError(null))}
          />
        );
    }
  };

  return (
    <div className="redesign self-registration-layout">
      <div className="self-registration-layout__header">
        <AuthCardLogo />
      </div>

      <div className="self-registration-layout__content">
        <div className="self-registration-layout__steps">
          <div className="self-registration-layout__progress-header">
            <span className="self-registration-layout__progress-label">
              Шаг {currentStepIndex + 1} из {TOTAL_STEPS} — {STEP_ITEMS[currentStepIndex].title}
            </span>
          </div>

          <div className="self-registration-layout__progress-bar">
            <div
              className="self-registration-layout__progress-fill"
              style={{ width: `${((currentStepIndex + 1) / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>

        <div className="self-registration-layout__step-content">{renderStep()}</div>
      </div>
    </div>
  );
};
